import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Checkbox } from "../ui/checkbox";
import {
  ShieldCheck,
  PiggyBank,
  Wallet,
  Layers,
  Droplets,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Sparkles,
  Info,
  Dice5,
  Crown,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useAuth } from "../../context/AuthContext";
import { api, fmtINR, formatApiError } from "../../lib/api";
import MoneyInput from "../ui/MoneyInput";
import DateField from "../ui/DateField";
import { toast } from "sonner";

const BAND_STYLES = {
  "On Track": {
    text: "text-emerald-700 dark:text-emerald-300",
    bg: "bg-emerald-50 dark:bg-emerald-500/10",
    bar: "bg-emerald-500",
    ring: "border-emerald-200/60 dark:border-emerald-500/20",
  },
  "Slightly Behind": {
    text: "text-amber-700 dark:text-amber-300",
    bg: "bg-amber-50 dark:bg-amber-500/10",
    bar: "bg-amber-500",
    ring: "border-amber-200/60 dark:border-amber-500/20",
  },
  "At Risk": {
    text: "text-rose-700 dark:text-rose-300",
    bg: "bg-rose-50 dark:bg-rose-500/10",
    bar: "bg-rose-500",
    ring: "border-rose-200/60 dark:border-rose-500/20",
  },
};

const band = (b) => BAND_STYLES[b] || BAND_STYLES["At Risk"];

const SEVERITY_ICON = {
  high: {
    Icon: AlertTriangle,
    cls: "text-rose-600 bg-rose-50 dark:bg-rose-500/10",
  },
  medium: {
    Icon: AlertTriangle,
    cls: "text-amber-600 bg-amber-50 dark:bg-amber-500/10",
  },
  low: { Icon: Lightbulb, cls: "text-sky-600 bg-sky-50 dark:bg-sky-500/10" },
  positive: {
    Icon: CheckCircle2,
    cls: "text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10",
  },
};

export default function PlanningSection() {
  const [overview, setOverview] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const [ov, pr] = await Promise.all([
        api.get("/planning/overview"),
        api.get("/profile/financial"),
      ]);
      setOverview(ov.data);
      setProfile(pr.data);
    } catch (e) {
      // silent - dashboard still renders other sections
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [ov, pr] = await Promise.all([
          api.get("/planning/overview"),
          api.get("/profile/financial"),
        ]);
        setOverview(ov.data);
        setProfile(pr.data);
      } catch (e) {
        // silent - dashboard still renders other sections
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <section className="mt-6 grid lg:grid-cols-3 gap-6 animate-pulse">
        <div className="h-44 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="h-44 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="h-44 rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </section>
    );
  }

  if (!overview) return null;

  const {
    reservation,
    retirement,
    financial_independence: fi,
    insights,
    assumption_version,
  } = overview;

  return (
    <div data-testid="planning-section" className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Wealth Planning Engine
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Availability, Retirement &amp; FI
          </h2>
        </div>
        <Link
          to="/dashboard/assumptions"
          data-testid="planning-adjust-assumptions"
          className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 hover:text-emerald-600 text-slate-500 px-2.5 py-1 rounded-full font-semibold transition-colors"
          title="Every figure is produced by the single projection engine and a versioned assumption set. Click to adjust."
        >
          <Info className="h-3 w-3" /> Engine v{assumption_version} · Adjust
          assumptions
        </Link>
      </div>

      {/* Wealth availability tiles */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <AvailTile
          icon={Wallet}
          label="Total Net Worth"
          value={fmtINR(reservation.total_net_worth)}
          sub="Assets − Liabilities"
          testid="avail-total-nw"
        />
        <AvailTile
          icon={Lock}
          label="Goal-Committed"
          value={fmtINR(reservation.goal_committed_wealth)}
          sub="Reserved across goals"
          accent="text-violet-600"
          testid="avail-committed"
        />
        <AvailTile
          icon={Layers}
          label="Available Net Worth"
          value={fmtINR(reservation.available_net_worth)}
          sub="Free to commit"
          accent="text-emerald-600"
          testid="avail-available-nw"
        />
        <AvailTile
          icon={Droplets}
          label="Available Liquid"
          value={fmtINR(reservation.available_liquid_wealth)}
          sub="Unreserved & liquid"
          accent="text-sky-600"
          testid="avail-liquid"
        />
      </section>

      <section className="grid lg:grid-cols-2 gap-6 mt-6">
        <RetirementCard
          retirement={retirement}
          profile={profile}
          onSaved={reload}
        />
        <FICard fi={fi} retirement={retirement} />
      </section>

      <MonteCarloCard />

      <InsightsCard insights={insights} />
    </div>
  );
}

function MonteCarloCard() {
  const { user } = useAuth();
  const isPremium = (user?.plan || "free").toLowerCase() !== "free";
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.get("/planning/monte-carlo", {
        params: { paths: 1000, horizon_years: 25 },
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  if (!isPremium) {
    return (
      <div className="card-soft p-6 mt-6">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50 dark:bg-amber-500/10 text-amber-600">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white">
                Success probability
              </h3>
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white">
                Premium
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500 max-w-xl">
              Run 1,000 market simulations to see the odds your plan actually
              works — and the range of outcomes you could land in.
            </p>
            <Link
              to="/dashboard/settings"
              className="btn-primary !text-xs mt-4 inline-flex"
            >
              <Crown className="h-4 w-4" /> Unlock with Premium
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const fan = (data?.yearly_percentiles || []).map((r) => ({
    year: r.year,
    p10: r.p10,
    p50: r.p50,
    p90: r.p90,
  }));
  const fnw = data?.final_net_worth;

  return (
    <div className="card-soft p-6 mt-6" data-testid="monte-carlo-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
            <Dice5 className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white">
              Success probability
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {data?.disclaimer ||
                "Monte Carlo simulation across 1,000 market paths."}
            </p>
          </div>
        </div>
        <button
          onClick={run}
          disabled={running}
          className="btn-secondary !text-xs whitespace-nowrap"
        >
          <Dice5 className="h-3.5 w-3.5" />
          {running ? "Simulating…" : data ? "Re-run" : "Run 1,000 sims"}
        </button>
      </div>

      {!data ? (
        <p className="mt-6 text-sm text-slate-500">
          Run the simulation to see your odds and outcome range.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <Metric
              label="Retirement success"
              value={
                data.fi_success_pct == null
                  ? "—"
                  : `${Math.round(data.fi_success_pct)}%`
              }
              accent={
                (data.fi_success_pct ?? 0) >= 75
                  ? "text-emerald-600"
                  : (data.fi_success_pct ?? 0) >= 50
                    ? "text-amber-600"
                    : "text-rose-600"
              }
            />
            <Metric
              label="Worst case (P10)"
              value={fmtINR(fnw?.p10, { compact: true })}
            />
            <Metric
              label="Median (P50)"
              value={fmtINR(fnw?.p50, { compact: true })}
              accent="text-emerald-600"
            />
            <Metric
              label="Best case (P90)"
              value={fmtINR(fnw?.p90, { compact: true })}
            />
          </div>

          {fan.length > 1 && (
            <div className="h-56 mt-5">
              <ResponsiveContainer>
                <AreaChart data={fan}>
                  <defs>
                    <linearGradient id="mcBand" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="#10b981"
                        stopOpacity={0.25}
                      />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="rgba(148,163,184,0.2)"
                  />
                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 11 }}
                    stroke="rgba(100,116,139,0.5)"
                  />
                  <YAxis
                    tickFormatter={(v) => fmtINR(v, { compact: true })}
                    tick={{ fontSize: 11 }}
                    stroke="rgba(100,116,139,0.5)"
                    width={56}
                  />
                  <Tooltip
                    formatter={(v, n) => [fmtINR(v), n.toUpperCase()]}
                    labelFormatter={(l) => `Year ${l}`}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="p90"
                    stroke="none"
                    fill="url(#mcBand)"
                    name="P90"
                  />
                  <Area
                    type="monotone"
                    dataKey="p10"
                    stroke="none"
                    fill="#fff"
                    fillOpacity={0}
                    name="P10"
                  />
                  <Area
                    type="monotone"
                    dataKey="p50"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fill="none"
                    name="P50"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="mt-2 text-[11px] text-slate-400">
            Shaded area spans pessimistic (P10) to optimistic (P90); the line is
            the median projected net worth.
          </p>
        </>
      )}
    </div>
  );
}

function AvailTile({
  icon: Icon,
  label,
  value,
  sub,
  accent = "text-slate-900 dark:text-white",
  testid,
}) {
  return (
    <div data-testid={testid} className="card-soft p-5">
      <div className="flex items-center justify-between">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-[10px] text-slate-500 uppercase tracking-wider text-right">
          {sub}
        </span>
      </div>
      <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide">
        {label}
      </div>
      <div className={`mt-1 num-mono text-2xl font-bold ${accent}`}>
        {value}
      </div>
    </div>
  );
}

function ProgressBar({ pct, barClass }) {
  return (
    <div className="h-2.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
      <div
        className={`h-full ${barClass} transition-all`}
        style={{ width: `${Math.min(pct || 0, 100)}%` }}
      />
    </div>
  );
}

function RetirementCard({ retirement, profile, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const buildForm = useCallback(
    () => ({
      current_age: profile?.current_age ?? "",
      retirement_age: profile?.retirement_age ?? 60,
      life_expectancy: profile?.life_expectancy ?? 85,
      current_annual_expenses: profile?.current_annual_expenses ?? "",
      include_unreserved_in_retirement:
        profile?.include_unreserved_in_retirement ?? true,
    }),
    [profile],
  );

  const openEditor = () => {
    setForm(buildForm());
    setEditing(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        retirement_age: form.retirement_age
          ? Number(form.retirement_age)
          : undefined,
        life_expectancy: form.life_expectancy
          ? Number(form.life_expectancy)
          : undefined,
        include_unreserved_in_retirement:
          !!form.include_unreserved_in_retirement,
      };
      if (form.current_age !== "")
        payload.current_age = Number(form.current_age);
      if (form.current_annual_expenses !== "")
        payload.current_annual_expenses = Number(
          String(form.current_annual_expenses).replace(/,/g, ""),
        );
      await api.put("/profile/financial", payload);
      toast.success("Retirement plan updated");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const needsInput = retirement?.needs_expenses || retirement?.needs_age;
  const st = band(retirement?.readiness_band);
  const showForm = editing || needsInput;

  // The form can be shown either by clicking Edit or automatically when the
  // engine still needs inputs (new user). Make sure `form` is populated in both
  // cases so the Editor never reads from a null form.
  useEffect(() => {
    if (showForm && !form) {
      setForm(buildForm());
    }
  }, [showForm, form, buildForm]);

  return (
    <div className="card-soft p-6" data-testid="retirement-card">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
          <PiggyBank className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Retirement Readiness
          </div>
          <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mt-1">
            {retirement?.retirement_age
              ? `Retire at ${retirement.retirement_age}`
              : "Retirement plan"}
          </h3>
          {retirement?.years_to_retirement != null && (
            <p className="text-xs text-slate-500 mt-1">
              {retirement.years_to_retirement} years to go ·{" "}
              {retirement.drawdown_years} year drawdown
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {retirement?.readiness_band && !showForm && (
            <span
              className={`text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full ${st.bg} ${st.text}`}
            >
              {retirement.readiness_band}
            </span>
          )}
          {!needsInput && (
            <button
              onClick={() => (editing ? setEditing(false) : openEditor())}
              data-testid="retire-edit-btn"
              className="text-[10px] uppercase tracking-widest bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 hover:text-emerald-600 text-slate-500 px-2.5 py-1 rounded-full font-semibold transition-colors"
            >
              {editing ? "Close" : "Edit"}
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div className="mt-5 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-4">
          {needsInput && !editing && (
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">
              <Sparkles className="h-4 w-4 text-emerald-600" /> Tell us a little
              about you
            </div>
          )}
          <p className="text-xs text-slate-500 mb-3">
            We compute your corpus from these — change any of them anytime.
          </p>
          {form && (
            <>
              <Editor
                form={form}
                setForm={setForm}
                retirement={retirement}
                profile={profile}
              />
              <button
                data-testid="retire-save-btn"
                onClick={saveProfile}
                disabled={saving}
                className="btn-primary w-full mt-3 !text-xs"
              >
                {saving ? "Saving…" : "Save & recompute"}
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <Metric
              label="Corpus Needed"
              value={fmtINR(retirement.required_corpus, { compact: true })}
            />
            <Metric
              label="Projected (net of loans)"
              value={fmtINR(retirement.projected_corpus, { compact: true })}
              accent="text-emerald-600"
            />
            <Metric
              label="Corpus Today"
              value={fmtINR(retirement.current_corpus, { compact: true })}
            />
            <Metric
              label={retirement.retirement_gap > 0 ? "Shortfall" : "Surplus"}
              value={fmtINR(Math.abs(retirement.retirement_gap), {
                compact: true,
              })}
              accent={
                retirement.retirement_gap > 0
                  ? "text-rose-600"
                  : "text-emerald-600"
              }
            />
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1.5">
              <span>
                On track to {Math.min(retirement.funding_pct, 100)}% of your
                goal
              </span>
              <span className={st.text}>
                {Math.min(retirement.funding_pct, 100)}%
              </span>
            </div>
            <ProgressBar pct={retirement.funding_pct} barClass={st.bar} />
          </div>
          {retirement.liabilities_at_retirement > 0 && (
            <p className="mt-2 text-[11px] text-slate-400">
              After clearing{" "}
              {fmtINR(retirement.liabilities_at_retirement, { compact: true })}{" "}
              of loans still outstanding at retirement.
            </p>
          )}
          {retirement.retirement_gap > 0 &&
            retirement.additional_monthly_investment > 0 && (
              <p className="mt-3 text-xs text-slate-500">
                Invest about{" "}
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {fmtINR(retirement.additional_monthly_investment)}/mo
                </span>{" "}
                more to retire comfortably.
              </p>
            )}
        </>
      )}
    </div>
  );
}

function Editor({ form, setForm }) {
  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  return (
    <div className="grid grid-cols-2 gap-3">
      <LabeledInput
        label="Current age"
        testid="retire-age-input"
        type="number"
        value={form.current_age}
        onChange={(v) => f("current_age", v)}
        placeholder="35"
      />
      <div>
        <label className="text-xs text-slate-500">
          Date of birth (optional)
        </label>
        <DateField
          className="mt-1"
          max={new Date().toISOString().slice(0, 10)}
          toYear={new Date().getFullYear()}
          value={form.date_of_birth || ""}
          onChange={(iso) => {
            let age = form.current_age;
            if (iso) {
              const d = new Date(iso);
              const now = new Date();
              age = now.getFullYear() - d.getFullYear();
              const m = now.getMonth() - d.getMonth();
              if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
            }
            setForm((s) => ({
              ...s,
              date_of_birth: iso,
              current_age: iso && age > 0 ? age : s.current_age,
            }));
          }}
        />
      </div>
      <LabeledInput
        label="Retire at age"
        type="number"
        value={form.retirement_age}
        onChange={(v) => f("retirement_age", v)}
        placeholder="60"
      />
      <LabeledInput
        label="Live until age"
        type="number"
        value={form.life_expectancy}
        onChange={(v) => f("life_expectancy", v)}
        placeholder="85"
      />
      <div>
        <label className="text-xs text-slate-500">
          Annual expenses (₹, today)
        </label>
        <div className="mt-1">
          <MoneyInput
            data-testid="retire-expenses-input"
            value={form.current_annual_expenses}
            onValueChange={(v) => f("current_annual_expenses", v)}
            placeholder="12,00,000"
          />
        </div>
      </div>
      <label className="col-span-2 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 mt-1">
        <Checkbox
          checked={!!form.include_unreserved_in_retirement}
          onCheckedChange={(checked) =>
            f("include_unreserved_in_retirement", checked === true)
          }
        />
        Count my unreserved investments toward retirement
      </label>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  testid,
}) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-soft mt-1"
        placeholder={placeholder}
      />
    </div>
  );
}

function FICard({ fi, retirement }) {
  const locked = fi?.fi_number == null;
  const st = band(fi?.fi_band);
  return (
    <div className="card-soft p-6" data-testid="fi-card">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Financial Independence
          </div>
          <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mt-1">
            Your FI number
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Can your wealth sustain your lifestyle &amp; obligations?
          </p>
        </div>
        {fi?.fi_band && (
          <span
            className={`text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full ${st.bg} ${st.text}`}
          >
            {fi.fi_band}
          </span>
        )}
      </div>

      {locked ? (
        <div className="mt-5 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-5 text-center">
          <Lock className="h-6 w-6 text-slate-400 mx-auto" />
          <p className="text-sm text-slate-500 mt-2">
            Add your annual expenses in the Retirement card to unlock your FI
            number.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <Metric
              label="FI Number"
              value={fmtINR(fi.fi_number, { compact: true })}
            />
            <Metric
              label="Effective Wealth"
              value={fmtINR(fi.current_effective_wealth, { compact: true })}
              accent="text-blue-600"
            />
            <Metric
              label="FI Progress"
              value={`${fi.fi_progress_pct}%`}
              accent={st.text}
            />
            <Metric
              label="Years to FI"
              value={fi.years_to_fi == null ? "—" : `${fi.years_to_fi} yrs`}
            />
          </div>
          <div className="mt-4">
            <ProgressBar pct={fi.fi_progress_pct} barClass={st.bar} />
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
            {fi.definition}
          </p>
        </>
      )}
    </div>
  );
}

function InsightsCard({ insights }) {
  if (!insights || insights.length === 0) return null;
  return (
    <div className="card-soft p-6 mt-6" data-testid="insights-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50 dark:bg-amber-500/10 text-amber-600">
          <Lightbulb className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Financial Insights
          </div>
          <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mt-1">
            Personalized, data-driven
          </h3>
        </div>
      </div>
      <div className="space-y-3">
        {insights.map((ins, i) => {
          const sev = SEVERITY_ICON[ins.severity] || SEVERITY_ICON.low;
          const { Icon } = sev;
          return (
            <div
              key={i}
              data-testid={`insight-${i}`}
              className="flex items-start gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 p-4"
            >
              <div
                className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${sev.cls}`}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 dark:text-white text-sm">
                  {ins.title}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  {ins.message}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Metric({ label, value, accent = "text-slate-900 dark:text-white" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className={`num-mono text-lg font-bold mt-0.5 ${accent}`}>
        {value}
      </div>
    </div>
  );
}
